import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Shape returned by GET /api/StaticData/FeatureFlags.
 * Mirrors TaskManager.Domain.Configuration.FeatureFlagsOptions on the server.
 */
export interface IFeatureFlags {
  region: string;
}

export const DEPLOYMENT_REGIONS = {
  US: 'US',
  EU: 'EU',
} as const;

/** Envelope shape returned by this API's endpoints — see IApiResponse in api.service.ts. */
interface IApiResponse<T> {
  message: string;
  result: T;
}

const DEFAULT_FLAGS: IFeatureFlags = {
  region: DEPLOYMENT_REGIONS.US,
};

interface IFlagsCacheEntry {
  flags: IFeatureFlags;
  cachedAt: number;
}

/** localStorage key used for the flags cache — exported for test teardown. */
export const FEATURE_FLAGS_CACHE_KEY = 'credithub.ff.v1';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Loads and caches runtime feature flags from the TaskManager API.
 *
 * The flags are fetched once during application bootstrap (see the
 * APP_INITIALIZER wiring in `app.module.ts`) and synchronously readable
 * thereafter via `flags` / `isEu()`.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private _flags: IFeatureFlags = DEFAULT_FLAGS;
  private _loaded = false;

  constructor(private readonly http: HttpClient) {}

  /**
   * Fetches the flags, using a short-lived localStorage cache to avoid
   * a network round-trip on repeat page loads. Falls back to defaults on
   * any network or parse error.
   */
  async load(): Promise<void> {
    if (this._loaded) return;

    // Serve from cache when still fresh to skip the network round-trip
    try {
      const raw = localStorage.getItem(FEATURE_FLAGS_CACHE_KEY);
      if (raw) {
        const entry: IFlagsCacheEntry = JSON.parse(raw);
        if (Date.now() - entry.cachedAt < CACHE_TTL_MS) {
          this._flags = { ...DEFAULT_FLAGS, ...entry.flags };
          this._loaded = true;
          return;
        }
      }
    } catch {
      // malformed cache entry — proceed to network fetch
    }

    try {
      const response = await firstValueFrom(
        this.http.get<IApiResponse<IFeatureFlags>>(
          `${environment.taskManagerServiceEndPoint}/StaticData/FeatureFlags`
        )
      );
      this._flags = { ...DEFAULT_FLAGS, ...(response?.result ?? {}) };
      try {
        const entry: IFlagsCacheEntry = { flags: this._flags, cachedAt: Date.now() };
        localStorage.setItem(FEATURE_FLAGS_CACHE_KEY, JSON.stringify(entry));
      } catch {
        // quota exceeded or private browsing — cache miss is safe to ignore
      }
    } catch {
      this._flags = DEFAULT_FLAGS;
    } finally {
      this._loaded = true;
    }
  }

  get flags(): IFeatureFlags {
    return this._flags;
  }

  get region(): string {
    return this._flags.region;
  }

  isEu(): boolean {
    return (this._flags.region ?? '').toUpperCase() === DEPLOYMENT_REGIONS.EU;
  }

  /** Angular/DevExtreme date format token (dd/MM/yyyy for EU, MM/dd/yyyy otherwise). */
  getDateDisplayFormat(): string {
    return this.isEu() ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
  }

  /** ISO 4217 code used when an amount has no explicit currency (GBP for EU, USD otherwise). */
  getDefaultCurrency(): string {
    return this.isEu() ? 'GBP' : 'USD';
  }
}
